import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Check, Copy, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { getScenarioId } from '@/Config/auth/authStore';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';

const DRUPAL_INTEGRATION_NAME = 'drupal' as const;
const DRUPAL_QUERY_KEY = [QueryKey.integration.get, DRUPAL_INTEGRATION_NAME] as const;

export interface DrupalIntegrationParams {
  drupal_integration_active: boolean;
  drupal_integration_token: string;
}

export interface DrupalIntegrationResponse {
  name: typeof DRUPAL_INTEGRATION_NAME;
  params: DrupalIntegrationParams;
}

const drupalFormSchema = z.object({
  drupal_integration_active: z.boolean(),
});

type DrupalFormValues = z.infer<typeof drupalFormSchema>;

interface DrupalSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const COPY_RESET_MS = 2000;

interface CopyFieldProps {
  label: string;
  value: string;
  testId: string;
  copyTestId: string;
}

const CopyField = ({ label, value, testId, copyTestId }: CopyFieldProps) => {
  const { t } = useTranslation(['integration']);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(t('integration:drupal.copied'));
      setTimeout(() => setCopied(false), COPY_RESET_MS);
    } catch {
      toast.error(t('integration:drupal.copy_failed'));
    }
  }, [value, t]);

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="text"
          size="sm"
          value={value}
          readOnly
          className="flex-1 !bg-muted font-mono text-sm"
          data-testid={testId}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCopy}
          data-testid={copyTestId}
          className="whitespace-nowrap"
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? t('integration:drupal.copied') : t('integration:drupal.copy')}
        </Button>
      </div>
    </div>
  );
};

export const DrupalSheet = ({ open, onOpenChange }: DrupalSheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const scenarioId = getScenarioId();

  const { data, isLoading } = useQuery<DrupalIntegrationResponse>({
    queryKey: DRUPAL_QUERY_KEY,
    enabled: open,
  });

  const { mutate: patchDrupal, isPending: isSaving } = useMutation<
    DrupalIntegrationResponse,
    AxiosError<{ detail?: string }>,
    { name: typeof DRUPAL_INTEGRATION_NAME; params: Partial<DrupalIntegrationParams> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (responseData) => {
      queryClient.setQueryData(DRUPAL_QUERY_KEY, responseData);
      toast.success(t('integration:drupal.saved'));
    },
    onError: (error) => {
      const detail = error.response?.data?.detail;
      toast.error(detail ?? t('integration:drupal.save_failed'));
    },
  });

  const form = useForm<DrupalFormValues>({
    mode: 'onChange',
    resolver: zodResolver(drupalFormSchema),
    defaultValues: {
      drupal_integration_active: false,
    },
  });

  const { control, reset, watch } = form;
  const isActive = watch('drupal_integration_active');

  // Reset the form whenever fresh server data arrives, so toggling the Sheet
  // always reflects the latest backend state.
  useEffect(() => {
    if (data?.params) {
      reset({ drupal_integration_active: data.params.drupal_integration_active });
    }
  }, [data, reset]);

  const token = data?.params.drupal_integration_token ?? '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-3xl w-full h-full"
        tabIndex={undefined}
        data-testid="drupal-sheet"
      >
        <SheetHeader className="px-0.5">
          <SheetTitle data-testid="drupal-sheet-title">{t('integration:drupal.title')}</SheetTitle>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="drupal-sheet-close"
          >
            <X className="size-4" />
          </IconButton>
        </SheetHeader>

        <p className="text-sm text-muted-foreground px-0.5" data-testid="drupal-sheet-description">
          {t('integration:drupal.description')}
        </p>

        <div className="flex flex-col gap-6 overflow-y-auto px-0.5">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-4">
            <Label
              htmlFor="drupal_integration_active"
              className="text-sm font-medium text-foreground"
            >
              {t('integration:drupal.enable')}
            </Label>
            <Controller
              control={control}
              name="drupal_integration_active"
              render={({ field }) => (
                <Switch
                  id="drupal_integration_active"
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    patchDrupal({
                      name: DRUPAL_INTEGRATION_NAME,
                      params: { drupal_integration_active: checked },
                    });
                  }}
                  disabled={isLoading || isSaving}
                  data-testid="drupal-active-switch"
                />
              )}
            />
          </div>

          {isActive ? (
            <div className="flex flex-col gap-4">
              <CopyField
                label={t('integration:drupal.scenario_id.label')}
                value={scenarioId}
                testId="drupal-scenario-id-input"
                copyTestId="drupal-scenario-id-copy"
              />
              <CopyField
                label={t('integration:drupal.token.label')}
                value={token}
                testId="drupal-token-input"
                copyTestId="drupal-token-copy"
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="drupal-inactive-hint">
              {t('integration:drupal.inactive_hint')}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
